import React, { useState, useEffect } from 'react';
import { Save, Flame, ArrowLeft, Plus, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOIS = [
  { num: 1, label: 'Janvier' }, { num: 2, label: 'Février' }, { num: 3, label: 'Mars' },
  { num: 4, label: 'Avril' }, { num: 5, label: 'Mai' }, { num: 6, label: 'Juin' },
  { num: 7, label: 'Juillet' }, { num: 8, label: 'Août' }, { num: 9, label: 'Septembre' },
  { num: 10, label: 'Octobre' }, { num: 11, label: 'Novembre' }, { num: 12, label: 'Décembre' }
];

const AdminBaremesTICGN = () => {
  const navigate = useNavigate();
  const [baremes, setBaremes] = useState({});
  const [annees, setAnnees] = useState(['2023', '2024', '2025', '2026', '2027']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [showAddYear, setShowAddYear] = useState(false);
  const [newYear, setNewYear] = useState('');

  useEffect(() => { fetchBaremes(); }, []);

  const fetchBaremes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/baremes-ticgn`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setBaremes(data.data || {});
        setSource(data.source || '');

        // Extraire les années depuis les données
        const yearsSet = new Set();
        Object.values(data.data || {}).forEach(moisData => {
          if (typeof moisData === 'object') {
            Object.keys(moisData).forEach(year => yearsSet.add(year));
          }
        });
        if (yearsSet.size > 0) {
          setAnnees(Array.from(yearsSet).sort());
        }
      }
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleChange = (mois, annee, value) => {
    setBaremes(prev => ({
      ...prev,
      [mois]: { ...prev[mois], [annee]: parseFloat(value) || 0 }
    }));
    setHasChanges(true);
  };

  const handleAddYear = () => {
    const year = parseInt(newYear);
    if (!year || year < 2020 || year > 2040) {
      alert('Année invalide (2020-2040)');
      return;
    }
    if (annees.includes(newYear)) {
      alert('Cette année existe déjà');
      return;
    }

    const newAnnees = [...annees, newYear].sort();
    setAnnees(newAnnees);

    // Copier les valeurs de la dernière année existante
    const lastYear = annees[annees.length - 1];
    const newBaremes = { ...baremes };
    for (let m = 1; m <= 12; m++) {
      const key = String(m);
      if (!newBaremes[key]) newBaremes[key] = {};
      newBaremes[key][newYear] = newBaremes[key][lastYear] || 16.37;
    }

    setBaremes(newBaremes);
    setHasChanges(true);
    setShowAddYear(false);
    setNewYear('');
    alert(`Année ${year} ajoutée avec les valeurs copiées de ${lastYear}`);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/baremes-ticgn`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ baremes })
      });
      if (res.ok) { setSource('database'); setHasChanges(false); alert('Barèmes TICGN sauvegardés'); }
    } catch (e) { alert('Erreur: ' + e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F97316]"></div></div>;

  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-baremes-ticgn">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/admin')} className="p-2 hover:bg-[#F5F6F9] rounded-lg" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5 text-[#687175]" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[#191D1E]">Barèmes TICGN</h1>
          <p className="text-sm text-[#687175]">
            Tarifs en €/MWh — Gaz Naturel
            {source === 'database' && <span className="text-emerald-600 ml-1">(base de données)</span>}
            {hasChanges && <span className="text-amber-500 ml-2 font-medium">Modifications non sauvegardées</span>}
          </p>
        </div>
        <Flame className="w-6 h-6 text-[#F97316]" />
        <button onClick={() => setShowAddYear(true)}
          className="flex items-center gap-1.5 border border-[#F97316] text-[#F97316] px-3 py-2 rounded-lg text-sm hover:bg-[#FFF7ED]"
          data-testid="add-year-btn">
          <Plus className="w-4 h-4" /> Ajouter une année
        </button>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 bg-[#F97316] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#EA580C] disabled:opacity-50"
          data-testid="save-baremes-btn">
          <Save className="w-4 h-4" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
      </div>

      <div className="bg-white border border-[#BFC6CC] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F97316] text-white">
              <th className="px-4 py-3 text-left font-semibold">Mois</th>
              {annees.map(a => <th key={a} className="px-4 py-3 text-center font-semibold">{a}</th>)}
            </tr>
          </thead>
          <tbody>
            {MOIS.map(({ num, label }) => (
              <tr key={num} className="border-t border-[#BFC6CC] hover:bg-[#FFF7ED]">
                <td className="px-4 py-2 font-medium text-[#191D1E]">{label}</td>
                {annees.map(annee => (
                  <td key={annee} className="px-2 py-1">
                    <input type="number" step="0.01" min="0"
                      value={baremes[String(num)]?.[annee] ?? ''}
                      onChange={(e) => handleChange(String(num), annee, e.target.value)}
                      className="w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-center font-mono text-sm focus:ring-1 focus:ring-[#F97316]"
                      data-testid={`bareme-${num}-${annee}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-[#687175]">
        Note : La TICGN s'applique de manière uniforme (pas de catégorie de tension). Un seul tarif par mois/année.
      </p>

      {/* Modal Ajouter Année */}
      {showAddYear && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#191D1E]">Ajouter une Année</h2>
              <button onClick={() => { setShowAddYear(false); setNewYear(''); }} className="p-1 hover:bg-[#F5F6F9] rounded">
                <X className="w-5 h-5 text-[#687175]" />
              </button>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#191D1E] mb-2">Année</label>
              <input type="number" min="2020" max="2040" value={newYear}
                onChange={(e) => setNewYear(e.target.value)} placeholder="Ex: 2028" autoFocus
                className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#F97316] font-mono text-lg"
                data-testid="new-year-input"
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddYear(); }} />
              <p className="text-xs text-[#687175] mt-2">
                Les barèmes de la dernière année ({annees[annees.length - 1]}) seront copiés comme valeurs initiales.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowAddYear(false); setNewYear(''); }}
                className="flex-1 px-6 py-3 border border-[#BFC6CC] text-[#687175] font-medium rounded-lg hover:bg-[#F5F6F9]">
                Annuler
              </button>
              <button onClick={handleAddYear}
                className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium py-3 px-6 rounded-lg"
                data-testid="confirm-add-year-btn">
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBaremesTICGN;
